import React, { useState, useEffect, useCallback } from 'react';
import { Bomb, Flag, Smile, Frown, Glasses } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const ROWS = 9;
const COLS = 9;
const MINES = 10;

const createBoard = () => {
  const board = Array(ROWS).fill(null).map(() =>
    Array(COLS).fill(null).map(() => ({
      isMine: false,
      isRevealed: false,
      isFlagged: false,
      neighborMines: 0
    }))
  );

  // Place mines randomly
  let minesPlaced = 0;
  while (minesPlaced < MINES) {
    const r = Math.floor(Math.random() * ROWS);
    const c = Math.floor(Math.random() * COLS);
    if (!board[r][c].isMine) {
      board[r][c].isMine = true;
      minesPlaced++;
    }
  }

  // Calculate neighbor counts
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (!board[r][c].isMine) {
        let count = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr, nc = c + dc;
            if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && board[nr][nc].isMine) {
              count++;
            }
          }
        }
        board[r][c].neighborMines = count;
      }
    }
  }

  return board;
};

export default function Minesweeper() {
  const [board, setBoard] = useState(createBoard);
  const [gameState, setGameState] = useState('playing'); // playing, won, lost
  const [flagsLeft, setFlagsLeft] = useState(MINES);
  const [time, setTime] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    let interval;
    if (started && gameState === 'playing') {
      interval = setInterval(() => setTime(t => Math.min(t + 1, 999)), 1000);
    }
    return () => clearInterval(interval);
  }, [started, gameState]);

  const revealCell = useCallback((r, c) => {
    if (gameState !== 'playing') return;
    if (board[r][c].isRevealed || board[r][c].isFlagged) return;

    if (!started) setStarted(true);

    const newBoard = board.map(row => row.map(cell => ({ ...cell })));

    if (newBoard[r][c].isMine) {
      // Game over - reveal all mines
      playSound(SOUNDS.error);
      for (let i = 0; i < ROWS; i++) {
        for (let j = 0; j < COLS; j++) {
          if (newBoard[i][j].isMine) newBoard[i][j].isRevealed = true;
        }
      }
      setBoard(newBoard);
      setGameState('lost');
      return;
    }

    // Flood fill reveal
    const reveal = (row, col) => {
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return;
      if (newBoard[row][col].isRevealed || newBoard[row][col].isFlagged) return;
      if (newBoard[row][col].isMine) return;

      newBoard[row][col].isRevealed = true;

      if (newBoard[row][col].neighborMines === 0) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            reveal(row + dr, col + dc);
          }
        }
      }
    };

    reveal(r, c);
    playSound(SOUNDS.click);

    // Check win condition
    let unrevealed = 0;
    for (let i = 0; i < ROWS; i++) {
      for (let j = 0; j < COLS; j++) {
        if (!newBoard[i][j].isRevealed) unrevealed++;
      }
    }
    if (unrevealed === MINES) {
      setGameState('won');
      playSound(SOUNDS.startup);
    }

    setBoard(newBoard);
  }, [board, gameState, started]);

  const toggleFlag = useCallback((e, r, c) => {
    e.preventDefault();
    if (gameState !== 'playing') return;
    if (board[r][c].isRevealed) return;

    const newBoard = board.map(row => row.map(cell => ({ ...cell })));
    newBoard[r][c].isFlagged = !newBoard[r][c].isFlagged;
    setFlagsLeft(f => newBoard[r][c].isFlagged ? f - 1 : f + 1);
    setBoard(newBoard);
    playSound(SOUNDS.click);
  }, [board, gameState]);

  const resetGame = () => {
    playSound(SOUNDS.click);
    setBoard(createBoard());
    setGameState('playing');
    setFlagsLeft(MINES);
    setTime(0);
    setStarted(false);
  };

  const numberColors = ['', '#0000FF', '#008000', '#FF0000', '#000080', '#800000', '#008080', '#000000', '#808080'];

  return (
    <div className="minesweeper">
      <div className="minesweeper-header">
        <div className="minesweeper-counter">{String(flagsLeft).padStart(3, '0')}</div>
        <button className="minesweeper-face" onClick={resetGame}>
          {gameState === 'playing' && <Smile size={20} />}
          {gameState === 'lost' && <Frown size={20} />}
          {gameState === 'won' && <Glasses size={20} />}
        </button>
        <div className="minesweeper-counter">{String(time).padStart(3, '0')}</div>
      </div>
      <div className="minesweeper-board">
        {board.map((row, r) => (
          <div key={r} className="minesweeper-row">
            {row.map((cell, c) => (
              <button
                key={c}
                className={`minesweeper-cell ${cell.isRevealed ? 'revealed' : ''} ${cell.isMine && cell.isRevealed ? 'mine' : ''}`}
                onClick={() => revealCell(r, c)}
                onContextMenu={(e) => toggleFlag(e, r, c)}
              >
                {cell.isFlagged && !cell.isRevealed && <Flag size={12} className="flag" />}
                {cell.isRevealed && cell.isMine && <Bomb size={12} />}
                {cell.isRevealed && !cell.isMine && cell.neighborMines > 0 && (
                  <span style={{ color: numberColors[cell.neighborMines] }}>
                    {cell.neighborMines}
                  </span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
